import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

import { CANDIDATE } from "@/data/content";
import { Marquee } from "@/components/campaign/Marquee";
import { MaskedText } from "@/components/campaign/Motion";

const NotFound = () => {
  return (
    <main
      data-testid="not-found-page"
      className="relative isolate overflow-hidden bg-graphite text-cream select-terra grain min-h-[100svh] flex flex-col"
    >
      {/* Layered spotlight background */}
      <div
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 45% at 82% 30%, rgba(232,122,47,0.32) 0%, rgba(10,25,48,0) 60%), radial-gradient(45% 40% at 12% 82%, rgba(20,63,124,0.55) 0%, rgba(10,25,48,0) 60%)",
        }}
      />

      {/* Rotating arc — signature detail, same language as home hero */}
      <motion.svg
        aria-hidden="true"
        animate={{ rotate: 360 }}
        transition={{ duration: 90, ease: "linear", repeat: Infinity }}
        viewBox="0 0 200 200"
        className="absolute -right-24 -top-24 h-[520px] w-[520px] opacity-50 pointer-events-none"
      >
        <circle
          cx="100"
          cy="100"
          r="88"
          fill="none"
          stroke="#F58A3F"
          strokeWidth="1.2"
          strokeDasharray="220 260"
        />
        <circle cx="100" cy="100" r="70" fill="none" stroke="#F58A3F" strokeOpacity="0.3" strokeWidth="1" />
        <circle cx="100" cy="100" r="54" fill="none" stroke="#FAF3E8" strokeOpacity="0.12" strokeWidth="1" />
      </motion.svg>

      {/* Top marquee */}
      <div className="pt-24 md:pt-28">
        <div className="border-y border-cream/15 py-4 font-serif italic text-2xl md:text-3xl text-cream/80">
          <Marquee
            speed={45}
            items={[
              "Error 404",
              "Wrong Door",
              "This Address Isn't On The Walk List",
              "Try Knocking Again",
              "Hillsborough 40",
              "ROB4NH",
              "Wrong Door",
            ]}
          />
        </div>
      </div>

      <div className="container-x flex-1 grid gap-12 md:grid-cols-12 items-center py-16 md:py-24">
        {/* Text column */}
        <div className="md:col-span-7 relative z-10" data-testid="not-found-copy">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
            className="inline-flex items-center gap-3 mb-8"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-terracotta opacity-70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-terracotta" />
            </span>
            <span className="font-mono text-[11px] tracking-[0.32em] uppercase text-cream/80">
              404 · Off The Walk List · {CANDIDATE.district}
            </span>
          </motion.div>

          <h1
            className="font-serif font-medium leading-[0.86] tracking-[-0.03em] text-[clamp(3.5rem,11vw,11rem)]"
            data-testid="not-found-title"
          >
            <MaskedText text="Wrong" as="span" className="block" delay={0.1} stagger={0.05} />
            <MaskedText
              text="door."
              as="span"
              className="block italic text-terracotta"
              delay={0.45}
              stagger={0.05}
            />
          </h1>

          <div className="mt-10 max-w-xl">
            <MaskedText
              text="Every door counts — but this one isn't on the map. Head back to the campaign and pick a real one to knock on."
              className="block font-serif text-lg md:text-xl leading-[1.45] text-cream/85"
              delay={1.0}
              stagger={0.015}
              duration={0.7}
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/"
              className="group inline-flex items-center gap-3 bg-terracotta text-graphite px-7 py-4 rounded-full font-semibold uppercase tracking-[0.18em] text-[12px] hover:bg-cream transition-colors duration-300"
              data-testid="not-found-home-link"
            >
              Back to the doors
              <span className="grid h-7 w-7 place-items-center rounded-full bg-graphite text-terracotta transition-transform duration-500 group-hover:rotate-45">
                <ArrowUpRight size={14} />
              </span>
            </Link>
            <Link
              to="/volunteer"
              className="group inline-flex items-center gap-2 font-mono uppercase text-[12px] tracking-[0.24em] text-cream/80 hover:text-terracotta transition-colors"
              data-testid="not-found-volunteer-link"
            >
              <span className="h-px w-8 bg-current transition-all duration-300 group-hover:w-12" />
              Or volunteer instead
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.1, duration: 1 }}
            className="mt-14 flex items-center gap-3 font-mono text-[10px] tracking-[0.4em] uppercase text-cream/50"
          >
            <span className="h-px w-10 bg-cream/30" />
            Return Address · Manchester, NH
          </motion.div>
        </div>

        {/* Clipped portrait */}
        <div className="md:col-span-5 relative z-10 flex justify-center md:justify-end">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="relative w-[min(80vw,380px)] aspect-[4/5]"
            data-testid="not-found-portrait"
          >
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0 0 0 0)" }}
              transition={{ duration: 1.6, ease: [0.19, 1, 0.22, 1], delay: 0.3 }}
              className="absolute inset-0 clip-editorial-alt overflow-hidden"
            >
              <motion.img
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 2.2, ease: [0.19, 1, 0.22, 1], delay: 0.3 }}
                src={CANDIDATE.portrait}
                alt="Robert Richard-Snipes"
                className="h-full w-full object-cover object-center"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 45% at 45% 30%, rgba(255,255,255,0) 0%, rgba(10,25,48,0.4) 75%, rgba(10,25,48,0.8) 100%)",
                }}
              />
            </motion.div>

            {/* Editorial stamp */}
            <motion.div
              initial={{ opacity: 0, y: 16, rotate: 6 }}
              animate={{ opacity: 1, y: 0, rotate: 6 }}
              transition={{ delay: 1.9, duration: 0.9 }}
              className="absolute -top-6 -right-4 border-2 border-terracotta text-terracotta px-4 py-2 font-mono text-[10px] uppercase tracking-[0.28em] bg-graphite/70 backdrop-blur"
            >
              Return · To · Sender
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.0, duration: 0.9 }}
              className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.28em] text-cream/70"
            >
              Fig. 404
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom marquee — closes the loop */}
      <div className="pb-16 md:pb-20">
        <div className="border-y border-cream/15 py-4 font-serif italic text-xl md:text-2xl text-cream/70">
          <Marquee
            speed={60}
            items={[
              "Strong Communities",
              "Secure Future",
              "Bold Solutions",
              "Robert Richard-Snipes",
              "State Representative",
              "Manchester, NH",
            ]}
          />
        </div>
      </div>
    </main>
  );
};

export default NotFound;
